/**
 * DtlPaperPage.js
 * DTL-2Stage 가상매매(Paper Trading) 대시보드
 *
 * Python paper_engine.py 가 Firestore 에 기록한 데이터를
 * /api/dtl-paper/* 를 통해 조회하여 표시합니다.
 *
 * 탭 구성:
 *   요약     — 누적 손익 / 승률 / 포지션 개요 + 일별 PnL 차트
 *   포지션   — 현재 보유 종목 목록
 *   주문내역 — 매수/매도 주문 이력
 *   청산내역 — 청산 이력
 *   로그     — 시스템 로그
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown,
  Activity, BarChart2, FileText, List, BookOpen
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell
} from 'recharts';
import apiService from '../../services/apiService';

/* ── 애니메이션 ─────────────────────────────────────────────────────────────── */
const fadeIn  = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const pulse   = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const spin    = keyframes`from{transform:rotate(0)}to{transform:rotate(360deg)}`;

/* ── 레이아웃 ────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height:100vh;background:#0a0a0f;color:#e2e8f0;
  font-family:'Inter','Pretendard',sans-serif;padding:20px;
`;
const TopBar = styled.div`
  display:flex;align-items:center;gap:12px;
  margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;
`;
const BackBtn = styled.button`
  background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;
  &:hover{background:#1e2030;color:#e2e8f0}
`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`
  font-size:22px;font-weight:800;margin:0;
  background:linear-gradient(135deg,#06b6d4,#8b5cf6);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;

const PaperBadge = styled.span`
  font-size:11px;font-weight:700;padding:3px 10px;border-radius:99px;
  background:rgba(6,182,212,0.12);color:#06b6d4;border:1px solid #06b6d4;
  animation:${pulse} 2s infinite;
`;

/* ── 탭 ─────────────────────────────────────────────────────────────────────── */
const NavTabs = styled.div`display:flex;gap:6px;flex-wrap:wrap;margin-bottom:24px;`;
const NavTab = styled.button`
  background:${p => p.$active ? 'rgba(6,182,212,0.12)' : 'transparent'};
  border:1px solid ${p => p.$active ? '#06b6d4' : '#1e2030'};
  color:${p => p.$active ? '#06b6d4' : '#64748b'};
  padding:7px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;
  display:flex;align-items:center;gap:5px;transition:all .2s;
  &:hover{border-color:#06b6d4;color:#06b6d4}
`;

/* ── 버튼 ────────────────────────────────────────────────────────────────────── */
const RefreshBtn = styled.button`
  display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;
  font-size:13px;font-weight:600;cursor:pointer;
  background:rgba(6,182,212,0.1);color:#06b6d4;border:1px solid #06b6d4;
  &:hover{opacity:.8}&:disabled{opacity:.4;cursor:not-allowed}
`;
const SpinIcon = styled(RefreshCw)`
  ${p => p.$spinning && css`animation:${spin} 1s linear infinite;`}
`;

/* ── 요약 카드 ───────────────────────────────────────────────────────────────── */
const StatGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:12px;
  margin-bottom:24px;animation:${fadeIn} .4s ease;
`;
const StatCard = styled.div`
  background:#111827;border-radius:14px;padding:18px 20px;
  border:1px solid ${p => p.$highlight ? p.$color || '#06b6d4' : '#1e2030'};
  box-shadow:${p => p.$highlight ? `0 0 18px ${p.$color || '#06b6d4'}22` : 'none'};
`;
const StatLabel = styled.p`margin:0 0 8px;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.06em;`;
const StatVal   = styled.div`
  font-size:26px;font-weight:800;
  color:${p => p.$pos === true ? '#10b981' : p.$pos === false ? '#ef4444' : '#e2e8f0'};
`;
const StatSub   = styled.p`margin:4px 0 0;font-size:11px;color:#475569;`;

/* ── 차트 ────────────────────────────────────────────────────────────────────── */
const ChartWrap = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:16px;
  padding:24px;margin-bottom:24px;animation:${fadeIn} .5s ease;
`;
const ChartTitle = styled.h3`margin:0 0 16px;font-size:14px;font-weight:700;color:#94a3b8;`;

/* ── 테이블 ──────────────────────────────────────────────────────────────────── */
const TableWrap = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;
  overflow:hidden;animation:${fadeIn} .4s ease;
`;
const TableHead = styled.div`
  padding:14px 16px;border-bottom:1px solid #1e2030;background:#0d1117;
  display:flex;align-items:center;gap:10px;flex-wrap:wrap;
`;
const TableTitle = styled.span`font-size:13px;font-weight:700;color:#e2e8f0;`;
const CountBadge = styled.span`
  font-size:11px;font-weight:700;padding:2px 9px;border-radius:99px;
  background:rgba(6,182,212,0.12);color:#06b6d4;
`;
const Overflow = styled.div`overflow-x:auto;`;

/* 공통 그리드 행 */
const buildGrid = cols => styled.div`
  display:grid;grid-template-columns:${cols};gap:8px;
  padding:10px 16px;font-size:12px;border-bottom:1px solid #1e2030;
  min-width:620px;align-items:center;
`;

/* 포지션 테이블 */
const POS_COLS  = '1fr 64px 90px 90px 90px 80px 64px 80px';
const PosTH     = buildGrid(POS_COLS);
const PosTR     = styled(PosTH)`
  transition:background .15s;animation:${fadeIn} .3s ease;
  &:hover{background:#0d1117}
  &:last-child{border-bottom:none}
`;

/* 주문 테이블 */
const ORD_COLS  = '1fr 64px 60px 90px 80px 70px 1fr';
const OrdTH     = buildGrid(ORD_COLS);
const OrdTR     = styled(OrdTH)`
  transition:background .15s;animation:${fadeIn} .3s ease;
  &:hover{background:#0d1117}&:last-child{border-bottom:none}
`;

/* 청산내역(trade-summary) 테이블 — 11열 */
const TS_COLS = '108px 140px 88px 58px 58px 88px 52px 74px 92px 1fr 1fr';
const TsTH = buildGrid(TS_COLS);
const TsTR = styled(TsTH)`
  transition:background .15s;animation:${fadeIn} .3s ease;
  background:${p => p.$active ? 'rgba(16,185,129,0.04)' : 'transparent'};
  &:hover{background:${p => p.$active ? 'rgba(16,185,129,0.08)' : '#0d1117'}}
  &:last-child{border-bottom:none}
  min-width:960px;
`;

/* 로그 테이블 */
const LOG_COLS  = '120px 70px 1fr';
const LogTH     = buildGrid(LOG_COLS);
const LogTR     = styled(LogTH)`
  transition:background .15s;
  &:hover{background:#0d1117}&:last-child{border-bottom:none}
  font-size:11px;
`;

const ThText = styled.span`font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.05em;`;
const MonoText = styled.span`font-family:'Consolas','Courier New',monospace;`;

/* ── Pnl 색상 헬퍼 ──────────────────────────────────────────────────────────── */
const pnlColor = v => (v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#64748b');
const fmtPnl   = v => (v == null ? '-' : `${v > 0 ? '+' : ''}${v.toLocaleString()}원`);
const fmtRate  = v => (v == null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
const fmtPrice = v => (v == null ? '-' : v.toLocaleString());
const fmtDate  = ts => {
  if (!ts) return '-';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

/* ── 빈 상태 ─────────────────────────────────────────────────────────────────── */
const Empty = styled.div`
  padding:48px;text-align:center;color:#475569;font-size:13px;
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   메인 컴포넌트
═══════════════════════════════════════════════════════════════════════════════ */
export default function DtlPaperPage() {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('summary');
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders]     = useState([]);
  const [exits, setExits]       = useState([]);
  const [logs, setLogs]         = useState([]);
  const [daily, setDaily]       = useState([]);
  const [error, setError]       = useState('');

  /* ── 데이터 로드 ──────────────────────────────────────────────────────────── */
  const load = useCallback(async (target = tab) => {
    setLoading(true);
    setError('');
    try {
      if (target === 'summary') {
        const [s, d] = await Promise.all([
          apiService.request('/dtl-paper/summary'),
          apiService.request('/dtl-paper/daily'),
        ]);
        setSummary(s);
        setDaily(d);
      } else if (target === 'positions') {
        setPositions(await apiService.request('/dtl-paper/positions'));
      } else if (target === 'orders') {
        setOrders(await apiService.request('/dtl-paper/orders?limit=200'));
      } else if (target === 'exits') {
        setExits(await apiService.request('/dtl-paper/trade-summary'));
      } else if (target === 'logs') {
        setLogs(await apiService.request('/dtl-paper/logs?limit=200'));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line

  /* ── 탭 전환 ──────────────────────────────────────────────────────────────── */
  const switchTab = t => {
    setTab(t);
    // load() will be called by the effect above
  };

  /* ── 요약 탭 ──────────────────────────────────────────────────────────────── */
  const SummaryTab = () => (
    <>
      <StatGrid>
        <StatCard $highlight={summary?.total_pnl !== 0} $color={summary?.total_pnl >= 0 ? '#10b981' : '#ef4444'}>
          <StatLabel>누적 손익</StatLabel>
          <StatVal $pos={summary?.total_pnl > 0 ? true : summary?.total_pnl < 0 ? false : undefined}>
            {summary ? fmtPnl(summary.total_pnl) : '-'}
          </StatVal>
          <StatSub>평균 {summary ? fmtRate(summary.avg_pnl_rate_pct) : '-'} / 건</StatSub>
        </StatCard>

        <StatCard>
          <StatLabel>승률</StatLabel>
          <StatVal $pos={summary?.win_rate >= 50 ? true : summary?.win_rate < 50 ? false : undefined}>
            {summary ? `${summary.win_rate}%` : '-'}
          </StatVal>
          <StatSub>{summary ? `${summary.wins}승 / ${summary.total_exits}건` : '-'}</StatSub>
        </StatCard>

        <StatCard>
          <StatLabel>현재 포지션</StatLabel>
          <StatVal>{summary ? summary.n_positions : '-'}</StatVal>
          <StatSub>투자금 {summary ? `${summary.total_invested.toLocaleString()}원` : '-'}</StatSub>
        </StatCard>

        <StatCard>
          <StatLabel>총 청산 건수</StatLabel>
          <StatVal>{summary ? summary.total_exits : '-'}</StatVal>
          <StatSub>Paper Trading Mode</StatSub>
        </StatCard>
      </StatGrid>

      <ChartWrap>
        <ChartTitle>일별 손익 (원)</ChartTitle>
        {daily.length === 0
          ? <Empty>아직 청산 데이터가 없습니다.</Empty>
          : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }}
                tickFormatter={v => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 8, fontSize: 12 }}
                formatter={(v, n) => [
                  n === 'pnl' ? fmtPnl(v) : v,
                  n === 'pnl' ? '손익' : n === 'trades' ? '거래수' : '승리'
                ]}
              />
              <ReferenceLine y={0} stroke="#374151" />
              <Bar dataKey="pnl" name="pnl" radius={[3,3,0,0]}>
                {daily.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.pnl >= 0 ? '#06b6d4' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrap>

      <ChartWrap>
        <ChartTitle>일별 거래 수 & 승리 수</ChartTitle>
        {daily.length === 0
          ? <Empty>아직 데이터가 없습니다.</Empty>
          : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={daily} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 8, fontSize: 12 }}
              />
              <Line type="monotone" dataKey="trades" name="거래수" stroke="#8b5cf6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="wins"   name="승리수" stroke="#10b981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartWrap>
    </>
  );

  /* ── 포지션 탭 ────────────────────────────────────────────────────────────── */
  const PositionsTab = () => (
    <TableWrap>
      <TableHead>
        <TableTitle>보유 포지션</TableTitle>
        <CountBadge>{positions.length}개</CountBadge>
      </TableHead>
      <Overflow>
        <PosTH>
          <ThText>종목명</ThText><ThText>시장</ThText>
          <ThText>평균단가</ThText><ThText>현재가</ThText>
          <ThText>평가손익</ThText><ThText>수익률</ThText>
          <ThText>수량</ThText><ThText>투자금액</ThText>
        </PosTH>
        {positions.length === 0 && <Empty>보유 포지션이 없습니다.</Empty>}
        {positions.map(p => {
          const unreal = p.current_price
            ? (p.current_price - p.avg_price) * p.qty
            : null;
          const rate = p.current_price && p.avg_price
            ? (p.current_price / p.avg_price - 1) * 100
            : null;
          return (
            <PosTR key={p.id}>
              <span style={{ fontWeight: 600 }}>{p.name || p.symbol}<br />
                <span style={{ fontSize: 10, color: '#64748b' }}>{p.symbol}</span>
              </span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>{p.market || '-'}</span>
              <MonoText>{fmtPrice(p.avg_price)}</MonoText>
              <MonoText>{fmtPrice(p.current_price)}</MonoText>
              <MonoText style={{ color: pnlColor(unreal) }}>{fmtPnl(unreal)}</MonoText>
              <MonoText style={{ color: pnlColor(rate) }}>{fmtRate(rate)}</MonoText>
              <span>{p.total_qty ?? p.qty ?? '-'}</span>
              <MonoText style={{ color: '#94a3b8' }}>{p.invested_amount ? p.invested_amount.toLocaleString() : '-'}</MonoText>
            </PosTR>
          );
        })}
      </Overflow>
    </TableWrap>
  );

  /* ── 주문내역 탭 ──────────────────────────────────────────────────────────── */
  const OrdersTab = () => (
    <TableWrap>
      <TableHead>
        <TableTitle>주문 이력</TableTitle>
        <CountBadge>{orders.length}건</CountBadge>
      </TableHead>
      <Overflow>
        <OrdTH>
          <ThText>종목</ThText><ThText>시장</ThText>
          <ThText>구분</ThText><ThText>체결가</ThText>
          <ThText>수량</ThText><ThText>금액</ThText>
          <ThText>사유</ThText>
        </OrdTH>
        {orders.length === 0 && <Empty>주문 내역이 없습니다.</Empty>}
        {orders.map(o => (
          <OrdTR key={o.id}>
            <span>{o.name || o.symbol}<br />
              <span style={{ fontSize: 10, color: '#64748b' }}>{fmtDate(o.created_at)}</span>
            </span>
            <span style={{ color: '#94a3b8', fontSize: 11 }}>{o.market || (o.stage ? `${o.stage}차` : '-')}</span>
            <SideBadge side={o.order_type}>{o.order_type === 'BUY' ? '매수' : '매도'}</SideBadge>
            <MonoText>{fmtPrice(o.filled_price)}</MonoText>
            <span>{o.filled_qty ?? '-'}</span>
            <MonoText>{o.filled_amount ? o.filled_amount.toLocaleString() : '-'}</MonoText>
            <span style={{ color: '#64748b', fontSize: 11, wordBreak: 'break-all' }}>{o.order_reason || '-'}</span>
          </OrdTR>
        ))}
      </Overflow>
    </TableWrap>
  );

  /* ── 청산내역 탭 ──────────────────────────────────────────────────────────── */
  const ExitsTab = () => {
    const activeCount = exits.filter(e => (e.position_qty ?? 0) > 0).length;
    const closedCount = exits.length - activeCount;
    return (
      <TableWrap>
        <TableHead>
          <TableTitle>매매 현황</TableTitle>
          {activeCount > 0 && <CountBadge style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid #10b981' }}>보유 {activeCount}</CountBadge>}
          {closedCount > 0 && <CountBadge>청산 {closedCount}</CountBadge>}
        </TableHead>
        <Overflow>
          <TsTH style={{ minWidth: 960 }}>
            <ThText>매매일시</ThText>
            <ThText>종목명(코드)</ThText>
            <ThText>매수가격</ThText>
            <ThText>매수수량</ThText>
            <ThText>매도수량</ThText>
            <ThText>매도가격</ThText>
            <ThText>잔량</ThText>
            <ThText>수익률</ThText>
            <ThText>수익금액</ThText>
            <ThText>매수사유</ThText>
            <ThText>매도사유</ThText>
          </TsTH>
          {exits.length === 0 && <Empty>매매 내역이 없습니다.</Empty>}
          {exits.map(e => {
            const isActive = (e.position_qty ?? 0) > 0;

            // 보유 중: EOD 종가 기준 미실현 손익 우선 표시, 없으면 실현손익
            // 청산 완료: 실현손익만 표시
            const hasEod     = isActive && e.eod_pnl_rate != null;
            const dispRate   = hasEod ? e.eod_pnl_rate * 100 : (e.realized_pnl_rate != null ? e.realized_pnl_rate * 100 : null);
            const dispAmount = hasEod ? e.eod_pnl_amount : e.realized_pnl_amount;
            const showPnl    = hasEod || (!isActive && e.total_sell_qty);

            return (
              <TsTR key={e.id} $active={isActive}>
                {/* 매매일시 */}
                <span style={{ color: '#64748b', fontSize: 11 }}>{fmtDate(e.updated_at)}</span>

                {/* 종목명(코드) + 보유/청산 배지 */}
                <span style={{ fontWeight: 600 }}>
                  {e.name || e.symbol}
                  <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>
                    {e.symbol}
                    {isActive
                      ? <span style={{ marginLeft: 4, color: '#10b981', fontWeight: 700 }}>●</span>
                      : <span style={{ marginLeft: 4, color: '#475569', fontWeight: 700 }}>○</span>
                    }
                  </span>
                </span>

                {/* 매수가격 */}
                <MonoText>{fmtPrice(e.avg_buy_price)}</MonoText>

                {/* 매수수량 */}
                <span>{e.total_buy_qty ?? '-'}</span>

                {/* 매도수량 */}
                <span>{e.total_sell_qty || '-'}</span>

                {/* 매도가격 / 보유중이면 EOD 종가 */}
                <MonoText>
                  {isActive && e.eod_price
                    ? <span title="EOD 종가">{fmtPrice(e.eod_price)}<span style={{ fontSize: 9, color: '#64748b', marginLeft: 2 }}>종가</span></span>
                    : e.avg_sell_price ? fmtPrice(e.avg_sell_price) : '-'}
                </MonoText>

                {/* 포지션 잔량 */}
                <span style={{ color: isActive ? '#10b981' : '#475569', fontWeight: isActive ? 700 : 400 }}>
                  {e.position_qty ?? '-'}
                </span>

                {/* 수익률 */}
                <MonoText style={{ color: pnlColor(dispRate) }}>
                  {showPnl && dispRate != null
                    ? <span>{fmtRate(dispRate)}{hasEod && <span style={{ fontSize: 9, color: '#64748b', marginLeft: 2 }}>종가</span>}</span>
                    : '-'}
                </MonoText>

                {/* 수익금액 */}
                <MonoText style={{ color: pnlColor(dispAmount) }}>
                  {showPnl && dispAmount != null ? fmtPnl(dispAmount) : '-'}
                </MonoText>

                {/* 매수사유 */}
                <span style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.latest_buy_reason || '-'}
                </span>

                {/* 매도사유 */}
                <span style={{ color: '#64748b', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.latest_sell_reason || '-'}
                </span>
              </TsTR>
            );
          })}
        </Overflow>
      </TableWrap>
    );
  };

  /* ── 로그 탭 ──────────────────────────────────────────────────────────────── */
  const LogsTab = () => (
    <TableWrap>
      <TableHead>
        <TableTitle>시스템 로그</TableTitle>
        <CountBadge>{logs.length}건</CountBadge>
      </TableHead>
      <Overflow>
        <LogTH>
          <ThText>시각</ThText><ThText>레벨</ThText><ThText>메시지</ThText>
        </LogTH>
        {logs.length === 0 && <Empty>로그가 없습니다.</Empty>}
        {logs.map(l => (
          <LogTR key={l.id}>
            <span style={{ color: '#64748b' }}>{fmtDate(l.created_at)}</span>
            <LevelBadge level={l.level}>{l.level || 'INFO'}</LevelBadge>
            <span style={{ color: '#94a3b8', wordBreak: 'break-all' }}>{l.message || '-'}</span>
          </LogTR>
        ))}
      </Overflow>
    </TableWrap>
  );

  /* ── 렌더 ─────────────────────────────────────────────────────────────────── */
  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/dtl-screener')}>
          <ArrowLeft size={18} />
        </BackBtn>
        <TitleArea>
          <PageTitle>DTL-2Stage 가상매매</PageTitle>
          <PageSub>Paper Trading Dashboard — Firestore 실시간 조회</PageSub>
        </TitleArea>
        <PaperBadge>PAPER MODE</PaperBadge>
        <RefreshBtn onClick={() => load(tab)} disabled={loading}>
          <SpinIcon size={14} $spinning={loading} />
          새로고침
        </RefreshBtn>
      </TopBar>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 13 }}>
          오류: {error}
        </div>
      )}

      <NavTabs>
        {[
          { key: 'summary',   icon: <BarChart2 size={13}/>,  label: '요약'     },
          { key: 'positions', icon: <Activity size={13}/>,   label: '포지션'   },
          { key: 'orders',    icon: <BookOpen size={13}/>,   label: '주문내역' },
          { key: 'exits',     icon: <TrendingUp size={13}/>, label: '청산내역' },
          { key: 'logs',      icon: <List size={13}/>,       label: '로그'     },
        ].map(t => (
          <NavTab key={t.key} $active={tab === t.key} onClick={() => switchTab(t.key)}>
            {t.icon}{t.label}
          </NavTab>
        ))}
      </NavTabs>

      {tab === 'summary'   && <SummaryTab />}
      {tab === 'positions' && <PositionsTab />}
      {tab === 'orders'    && <OrdersTab />}
      {tab === 'exits'     && <ExitsTab />}
      {tab === 'logs'      && <LogsTab />}
    </Page>
  );
}

/* ── 작은 배지 컴포넌트 ─────────────────────────────────────────────────────── */
const SideBadge = styled.span`
  display:inline-block;font-size:10px;font-weight:700;padding:2px 8px;border-radius:6px;
  background:${p => p.side === 'BUY' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)'};
  color:${p => p.side === 'BUY' ? '#10b981' : '#ef4444'};
`;
const LevelBadge = styled.span`
  display:inline-block;font-size:9px;font-weight:700;padding:2px 7px;border-radius:5px;
  background:${p => p.level === 'ERROR' ? 'rgba(239,68,68,0.12)' :
               p.level === 'WARNING' ? 'rgba(245,158,11,0.12)' : 'rgba(100,116,139,0.1)'};
  color:${p => p.level === 'ERROR' ? '#f87171' :
          p.level === 'WARNING' ? '#fbbf24' : '#64748b'};
`;
